import { Controller } from "@hotwired/stimulus";

/**
 * Pricing Controller - Price display & recurrence management

 * Responsibilities:
 * • Monthly/yearly recurrence switching & display
 * • Viewer dropdown pricing updates (premium plans)
 * • Price display from backend data attributes
 * • Currency formatting integration with CurrencyManage.js
 * • Inter-controller communication via plan-changed events

 * Replaces jQuery:
 * • CurrencyManage.updateCurrencyElements()
 * • Plan.updateViewerCount() pricing logic
 * • Monthly/yearly toggle functionality

 * Note: Price calculations moved to backend in a later PR
 */

export default class extends Controller {
  static DEFAULTS = {
    DEFAULT_RECURRENCE: "year",
  };

  static targets = [
    "monthlyPrice",
    "yearlyPrice",
    "billingText",
    "viewerSelect",
    "basicCard",
    "premiumCard",
  ];

  static values = {
    selectedPlan: String,
    recurrence: String,
    currentRecurrence: String,
    currentPlan: String,
  };

  connect() {
    this.recurrenceValue ||= this.currentRecurrenceValue || this.constructor.DEFAULTS.DEFAULT_RECURRENCE;
    this.toggleRecurrenceDisplay();
    this.initializeViewerCount();
    this.element.addEventListener("plans:plan-changed", this.handlePlanChanged);
    this.element.addEventListener("plans:recurrence-changed", this.handleRecurrence);
  }

  disconnect() {
    this.element.removeEventListener("plans:plan-changed", this.handlePlanChanged);
    this.element.removeEventListener("plans:recurrence-changed", this.handleRecurrence);
  }

  // Events

  handlePlanChanged = (event) => this.onPlanChanged(event.detail.planName);
  handleRecurrence = (event) => this.onRecurrenceChanged(event.detail.recurrence);

  // Inter-controller communication
  onPlanChanged(planName) {
    this.selectedPlanValue = planName;
    this.refreshPlanPrices();
  }

  onRecurrenceChanged(recurrence) {
    this.recurrenceValue = recurrence;
  }

  // Viewer count (premium dropdown)

  handleViewerSelection() {
    // Get selected option from the premium viewers dropdown
    const selectedOption = this.viewerSelectTarget?.selectedOptions?.[0];
    if (!selectedOption) return;

    // Extract pricing data from the selected dropdown option
    const { plan, pricemonth, priceyear } = selectedOption.dataset;

    // Update selected plan and notify other controllers
    this.selectedPlanValue = plan;
    this.dispatch("plan-changed", { detail: { planName: plan }, bubbles: true });

    // Update price display with new amounts
    this.setPriceAmountsOnCard(pricemonth, priceyear);

    // Trigger currency conversion to current selected currency
    this.updateCurrencyDisplay();
  }

  initializeViewerCount() {
    if (!this.hasViewerSelectTarget || !this.currentPlanValue) return;
    const option = this.viewerSelectTarget.querySelector(`option[data-plan="${this.currentPlanValue}"]`);
    if (option) this.viewerSelectTarget.value = option.value;
  }

  // Display updates

  toggleRecurrenceDisplay() {
    const isMonthly = this.recurrenceValue === "month";

    this.monthlyPriceTargets.forEach(price => price.classList.toggle("hidden", !isMonthly));
    this.yearlyPriceTargets.forEach(price => price.classList.toggle("hidden", isMonthly));

    this.billingTextTargets.forEach(text => {
      text.classList.toggle("billed-per-month", isMonthly);
      text.classList.toggle("billed-per-year", !isMonthly);
    });
  }

  refreshPlanPrices() {
    // Premium plans need price updates from dropdown
    if (this.selectedPlanValue.startsWith("premium_") && this.hasViewerSelectTarget) {
      const option = this.viewerSelectTarget.selectedOptions?.[0];
      if (option) {
        const { pricemonth, priceyear } = option.dataset;
        this.setPriceAmountsOnCard(pricemonth, priceyear);
      }
    }

    // Apply currency conversion after price update
    this.updateCurrencyDisplay();
  }

  setPriceAmountsOnCard(monthlyPrice, yearlyPrice, planType = null) {
    // Find the plan card to update (premium, basic, or auto-detect)
    const planCard =
      (planType === "premium" && this.premiumCardTarget) ||
      (planType === "basic" && this.basicCardTarget) ||
      this.element.querySelector(`[data-plan^="${planType || "premium"}"]`);

    // Prices from dropdown are EUR, but user may have DKK/USD selected
    // 1. Set textContent (shows EUR temporarily) & data-euro (for conversion)
    // 2. CurrencyManage.js converts data-euro to user's currency in updateCurrencyDisplay()
    this.updatePriceElements(planCard, "[data-pricing-target=\"monthlyAmount\"]", monthlyPrice);
    this.updatePriceElements(planCard, "[data-pricing-target=\"yearlyAmount\"]", yearlyPrice);
  }

  updatePriceElements(planCard, selector, price) {
    planCard.querySelectorAll(selector).forEach(el => {
      el.textContent = price;
      el.dataset.euro = price;
    });
  }

  updateCurrencyDisplay() {
    // Trigger currency conversion using existing CurrencyManage.js system
    if (window.CurrencyManage) CurrencyManage.updateCurrencyElements();
  }

  // Reactive values

  selectedPlanValueChanged() {
    // Only update pricing automatically for non-premium plans
    // Premium plans handle pricing updates via viewer dropdown
    if (!this.selectedPlanValue?.startsWith("premium_")) this.refreshPlanPrices();
  }

  recurrenceValueChanged() {
    // When recurrence changes (monthly/yearly), update display and pricing
    this.toggleRecurrenceDisplay();
    this.refreshPlanPrices();
  }
}
